/* Spin-to-Win wheel widget — vanilla JS, framework-agnostic.
 *
 * Mount:
 *   <link rel="stylesheet" href="/spin-wheel/widget.css">
 *   <div id="my-wheel"></div>
 *   <script src="/spin-wheel/widget.js"></script>
 *   <script>
 *     SpinWheel.mount(document.getElementById('my-wheel'), {
 *       apiEndpoint: '/api/spin',
 *       mode: 'inline',           // 'inline' or 'popup'
 *       popupTrigger: 'exit',     // 'exit' | 'delay' | 'manual' (popup only)
 *       popupDelayMs: 5000,       // for 'delay' trigger
 *       popupShowAgainHours: 168, // re-show popup after this many hours (0 = never re-show)
 *       title: 'Spin & Win!',
 *       subtitle: 'One spin per device. Enter your email to play.',
 *     });
 *   </script>
 *
 * Popup control:
 *   SpinWheel.openPopup();        // open programmatically (manual trigger)
 *   SpinWheel.closePopup();
 *
 * Released as IIFE — no dependencies. ~7 KB minified.
 */
(function (global) {
  'use strict';

  /* ---------------------------- Config ---------------------------- */
  var DEFAULTS = {
    apiEndpoint: '',
    useLocalResults: true,
    submitLeadCaptureForm: true,
    mode: 'inline',
    popupTrigger: 'exit',     // 'exit' | 'delay' | 'manual'  (default 'exit' — 'delay' auto-opens after popupDelayMs and locks scroll, which can feel like a 'halt')
    popupDelayMs: 8000,
    popupShowAgainHours: 168,  // 7 days; 0 = never re-show after first dismissal
    popupAutoCloseMs: 0,       // 0 = never auto-close; set to e.g. 60000 to auto-close after 60s
    title: 'Spin the CompuMe Wheel',
    subtitle: 'Enter your email and mobile to spin. One spin per device.',
    deviceKey: 'compume_spw_device_id',
    deviceIdProvider: null,      // optional function returning a UUID-formatted custom device id
    spunKey: 'compume_spw_spun_at',
    resultKey: 'compume_spw_result',
    dismissedKey: 'compume_spw_dismissed_at',
    capturePhone: true,
    phoneRequired: true,
    consentRequired: false,
    consentText: 'By spinning you agree to receive CompuMe email and SMS offers',
    leadCaptureFormSelector: '',
    leadTags: 'newsletter, compume-wheel, sms-optin-request',
    source: '',
    suppressNewsletterPopup: true,
    newsletterPopupCookie: 'compume-newsletter-popup-v2',
    newsletterPopupExpireDays: 30,
    onPopupOpen: null,         // callback fired when popup opens (replaces DOM polling)
    onPopupClose: null,        // callback fired when popup closes
    colors: {
      blue: '#0057D9',
      yellow: '#FFD400',
      navy: '#071D49',
      red: '#E1261C',
      white: '#FFFFFF',
      ice: '#EEF4FF'
    }
  };

  var DEVICE_ID_RE = /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i;

  /* ---------------------- Device fingerprint ---------------------- */
  // We use a persistent UUID in localStorage as a "device" identifier.
  // This is best-effort — a determined attacker can clear localStorage and
  // get a new ID — but it stops casual re-spinning. For stronger guarantees,
  // integrate FingerprintJS server-side.
  function getDeviceId(key) {
    try {
      var existing = localStorage.getItem(key);
      if (existing && DEVICE_ID_RE.test(existing)) {
        return existing;
      }
      var uuid = uuidv4();
      localStorage.setItem(key, uuid);
      return uuid;
    } catch (e) {
      // localStorage unavailable (private mode, cookies blocked)
      // Fallback to a session-only ID
      return uuidv4();
    }
  }

  function resolveDeviceId(config) {
    if (typeof config.deviceIdProvider === 'function') {
      try {
        var customId = String(config.deviceIdProvider() || '').trim().toLowerCase();
        if (DEVICE_ID_RE.test(customId)) return customId;
      } catch (e) {}
    }
    return getDeviceId(config.deviceKey);
  }

  function uuidv4() {
    // RFC 4122 v4 — uses crypto if available, Math.random fallback
    if (global.crypto && global.crypto.randomUUID) {
      return global.crypto.randomUUID();
    }
    var bytes = new Array(16);
    for (var i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
    // Set version (4) and variant (10xx)
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    var hex = bytes.map(function (b) { return ('0' + b.toString(16)).slice(-2); });
    return (
      hex.slice(0, 4).join('') + '-' +
      hex.slice(4, 6).join('') + '-' +
      hex.slice(6, 8).join('') + '-' +
      hex.slice(8, 10).join('') + '-' +
      hex.slice(10, 16).join('')
    );
  }

  function getStorageTs(key) {
    try {
      var v = localStorage.getItem(key);
      return v ? parseInt(v, 10) : 0;
    } catch (e) { return 0; }
  }
  function setStorageTs(key, ts) {
    try { localStorage.setItem(key, String(ts)); } catch (e) {}
  }

  function getStorageJson(key) {
    if (!key) return null;
    try {
      var value = localStorage.getItem(key);
      return value ? JSON.parse(value) : null;
    } catch (e) {
      return null;
    }
  }

  function setStorageJson(key, value) {
    if (!key) return;
    try { localStorage.setItem(key, JSON.stringify(value)); } catch (e) {}
  }

  function setCookie(cname, cvalue, exdays) {
    try {
      var d = new Date();
      d.setTime(d.getTime() + (exdays * 24 * 60 * 60 * 1000));
      document.cookie = cname + '=' + cvalue + ';expires=' + d.toUTCString() + ';path=/';
    } catch (e) {}
  }

  function suppressHaloNewsletterPopup(config) {
    if (!config.suppressNewsletterPopup || typeof document === 'undefined') return;
    try {
      document.body.dataset.compumeSuppressNewsletterPopup = 'true';
      document.body.classList.remove('newsletter-show', 'show-newsletter-image', 'newsletter-message-show');
      setCookie(config.newsletterPopupCookie, 'closed', config.newsletterPopupExpireDays);
    } catch (e) {}
  }

  function popupDismissedBlocked(config) {
    var dismissedTs = getStorageTs(config.dismissedKey);
    if (dismissedTs <= 0) return false;
    if (config.popupShowAgainHours === 0) return true;
    if (config.popupShowAgainHours > 0) {
      var elapsedH = (Date.now() - dismissedTs) / 3600000;
      return elapsedH < config.popupShowAgainHours;
    }
    return false;
  }

  /* ---------------------------- Icons ---------------------------- */
  var ICONS = {
    mail: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="5" width="18" height="14" rx="2"/><path d="m3 7 9 6 9-6"/></svg>',
    phone: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1 1 .4 1.9.7 2.8a2 2 0 0 1-.5 2.1L8.1 9.9a16 16 0 0 0 6 6l1.3-1.2a2 2 0 0 1 2.1-.5c.9.3 1.8.6 2.8.7A2 2 0 0 1 22 16.9z"/></svg>',
    arrow: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M5 12h14M13 6l6 6-6 6"/></svg>',
    lock: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="4" y="11" width="16" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/></svg>',
    close: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M18 6 6 18M6 6l12 12"/></svg>',
    gift: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="8" width="18" height="4" rx="1"/><path d="M12 8v13M5 12v9h14v-9M12 8S7 8 7 5a2 2 0 0 1 4 0v3M12 8s5 0 5-3a2 2 0 0 0-4 0v3"/></svg>',
    sparkle: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l1.8 6.2L20 10l-6.2 1.8L12 18l-1.8-6.2L4 10l6.2-1.8L12 2z"/></svg>',
    copy: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>',
    check: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><path d="M5 12l5 5L20 7"/></svg>',
    refresh: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 12a9 9 0 0 1 15-6.7L21 8M21 3v5h-5M21 12a9 9 0 0 1-15 6.7L3 16M3 21v-5h5"/></svg>',
  };

  /* --------------------------- Constants --------------------------- */
  // Must match server-side SEGMENTS in src/lib/wheel-config.ts
  var SEGMENT_MODEL = [
    { id: 'ONE_PCT',             label: '1% OFF',        color: 'blue',   textColor: 'white' },
    { id: 'ONE_POINT_FIVE_PCT',  label: '1.5% OFF',      color: 'yellow', textColor: 'navy' },
    { id: 'TWO_PCT',             label: '2% OFF',        color: 'white',  textColor: 'navy' },
    { id: 'TWO_POINT_FIVE_PCT',  label: '2.5% OFF',      color: 'red',    textColor: 'white' },
    { id: 'THREE_PCT',           label: '3% OFF',        color: 'blue',   textColor: 'white' },
    { id: 'FREE_DELIVERY',       label: 'FREE DELIVERY', color: 'yellow', textColor: 'navy' },
    { id: 'SORRY',               label: 'Sorry',         color: 'navy',   textColor: 'white' },
  ];
  var PRIZE_LABELS = {
    ONE_PCT: '1% OFF',
    ONE_POINT_FIVE_PCT: '1.5% OFF',
    TWO_PCT: '2% OFF',
    TWO_POINT_FIVE_PCT: '2.5% OFF',
    THREE_PCT: '3% OFF',
    FREE_DELIVERY: 'Free Delivery',
    SORRY: 'Sorry'
  };
  var PRIZE_RESULTS = {
    ONE_PCT: {
      label: '1% OFF',
      description: 'Use CompuMe Wheel 1% at checkout for 1% off eligible orders.',
      code: 'CompuMe Wheel 1%',
      isWinner: true
    },
    ONE_POINT_FIVE_PCT: {
      label: '1.5% OFF',
      description: 'Use CompuMe Wheel 1.5% at checkout for 1.5% off eligible orders.',
      code: 'CompuMe Wheel 1.5%',
      isWinner: true
    },
    TWO_PCT: {
      label: '2% OFF',
      description: 'Use CompuMe Wheel 2% at checkout for 2% off eligible orders.',
      code: 'CompuMe Wheel 2%',
      isWinner: true
    },
    TWO_POINT_FIVE_PCT: {
      label: '2.5% OFF',
      description: 'Use CompuMe Wheel 2.5% at checkout for 2.5% off eligible orders.',
      code: 'CompuMe Wheel 2.5%',
      isWinner: true
    },
    THREE_PCT: {
      label: '3% OFF',
      description: 'Use CompuMe Wheel 3% at checkout for 3% off eligible orders.',
      code: 'CompuMe Wheel 3%',
      isWinner: true
    },
    FREE_DELIVERY: {
      label: 'Free Delivery',
      description: 'Use CompuMe Wheel Free Delivery at checkout for free delivery in Jordan.',
      code: 'CompuMe Wheel Free Delivery',
      isWinner: true
    },
    SORRY: {
      label: 'Sorry',
      description: 'No discount this spin, but you are now on the CompuMe list for the next deal drop.',
      code: '',
      isWinner: false
    }
  };
  var WHEEL_DRAW_SIZE = 440; // canvas internal resolution (CSS scales it)

  function secureRandomInt(max) {
    if (!max || max <= 0) return 0;
    if (global.crypto && global.crypto.getRandomValues) {
      var value = new Uint32Array(1);
      global.crypto.getRandomValues(value);
      return value[0] % max;
    }
    return Math.floor(Math.random() * max);
  }

  function createLocalSpinResult(segments) {
    var winnerIndex = secureRandomInt(segments.length);
    var prizeId = segments[winnerIndex] && segments[winnerIndex].id;
    var result = PRIZE_RESULTS[prizeId] || PRIZE_RESULTS.SORRY;
    return {
      ok: true,
      winnerIndex: winnerIndex,
      prize: prizeId,
      label: result.label,
      description: result.description,
      code: result.code,
      isWinner: result.isWinner
    };
  }

  function normalizeColors(colors) {
    colors = colors || {};
    return {
      blue: colors.blue || colors.sky || DEFAULTS.colors.blue,
      yellow: colors.yellow || DEFAULTS.colors.yellow,
      navy: colors.navy || colors.darkblue || colors.black || DEFAULTS.colors.navy,
      red: colors.red || DEFAULTS.colors.red,
      white: colors.white || DEFAULTS.colors.white,
      ice: colors.ice || DEFAULTS.colors.ice
    };
  }

  function buildSegments(colors) {
    return SEGMENT_MODEL.map(function (seg) {
      return {
        id: seg.id,
        label: seg.label,
        color: colors[seg.color] || DEFAULTS.colors[seg.color],
        textColor: colors[seg.textColor] || DEFAULTS.colors[seg.textColor]
      };
    });
  }

  function applyColorVars(root, colors) {
    if (!root || !root.style) return;
    root.style.setProperty('--spw-blue', colors.blue);
    root.style.setProperty('--spw-yellow', colors.yellow);
    root.style.setProperty('--spw-navy', colors.navy);
    root.style.setProperty('--spw-red', colors.red);
    root.style.setProperty('--spw-white', colors.white);
    root.style.setProperty('--spw-ice', colors.ice);
    root.style.setProperty('--spw-sky', colors.blue);
    root.style.setProperty('--spw-darkblue', colors.navy);
    root.style.setProperty('--spw-black', colors.navy);
  }

  /* ----------------------- Canvas drawing ----------------------- */
  function drawWheel(ctx, rotation, segments, colors) {
    var size = WHEEL_DRAW_SIZE;
    var radius = size / 2;
    var segmentCount = segments.length;
    var segmentArc = (Math.PI * 2) / segmentCount;
    ctx.clearRect(0, 0, size, size);

    ctx.save();
    ctx.translate(radius, radius);
    ctx.rotate(rotation);

    // Outer ring
    ctx.beginPath();
    ctx.arc(0, 0, radius - 2, 0, Math.PI * 2);
    ctx.fillStyle = colors.navy;
    ctx.fill();

    for (var i = 0; i < segmentCount; i++) {
      var seg = segments[i];
      var startAngle = -Math.PI / 2 - segmentArc / 2 + i * segmentArc;
      var endAngle = startAngle + segmentArc;

      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.arc(0, 0, radius - 8, startAngle, endAngle);
      ctx.closePath();
      ctx.fillStyle = seg.color;
      ctx.fill();

      ctx.strokeStyle = colors.navy;
      ctx.lineWidth = 2;
      ctx.stroke();

      ctx.save();
      ctx.rotate(startAngle + segmentArc / 2);
      ctx.textAlign = 'right';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = seg.textColor;
      ctx.font = 'bold 16px Inter, system-ui, sans-serif';
      ctx.fillText(seg.label, radius - 22, 0);
      ctx.restore();
    }

    // Center hub
    ctx.beginPath();
    ctx.arc(0, 0, 26, 0, Math.PI * 2);
    ctx.fillStyle = colors.navy;
    ctx.fill();
    ctx.beginPath();
    ctx.arc(0, 0, 18, 0, Math.PI * 2);
    ctx.fillStyle = colors.yellow;
    ctx.fill();

    ctx.restore();
  }

  function setupCanvas(canvas) {
    var dpr = window.devicePixelRatio || 1;
    canvas.width = WHEEL_DRAW_SIZE * dpr;
    canvas.height = WHEEL_DRAW_SIZE * dpr;
    var ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Spin wheel canvas is not supported in this browser.');
    }
    ctx.scale(dpr, dpr);
    return ctx;
  }

  function animateSpin(ctx, winnerIndex, segments, colors, onDone) {
    var segmentArc = (Math.PI * 2) / segments.length;
    var safeWinnerIndex = Number(winnerIndex);
    if (!isFinite(safeWinnerIndex) || safeWinnerIndex < 0 || safeWinnerIndex >= segments.length) {
      safeWinnerIndex = 0;
    }
    var target = -safeWinnerIndex * segmentArc;
    var startRotation = 0; // wheel always starts at 0 on mount
    var delta = target - startRotation;
    delta = ((delta % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
    delta += Math.PI * 2 * 6; // 6 extra full turns

    var duration = 5200;
    var t0 = performance.now();

    function easeOut(t) { return 1 - Math.pow(1 - t, 4); }

    function tick(now) {
      var t = Math.min(1, (now - t0) / duration);
      var eased = easeOut(t);
      drawWheel(ctx, startRotation + delta * eased, segments, colors);
      if (t < 1) {
        requestAnimationFrame(tick);
      } else {
        onDone();
      }
    }
    requestAnimationFrame(tick);
  }

  /* --------------------------- Widget --------------------------- */
  function createWidget(root, opts) {
    var config = Object.assign({}, DEFAULTS, opts);
    config.colors = normalizeColors(config.colors);
    var segments = buildSegments(config.colors);
    var deviceId = resolveDeviceId(config);

    // State machine
    var state = {
      phase: 'email',    // 'email' | 'ready' | 'spinning' | 'revealed'
      email: '',
      phone: '',
      busy: false,
      result: null,
      error: null,
    };

    root.classList.add('spw-root');
    applyColorVars(root, config.colors);
    suppressHaloNewsletterPopup(config);

    // Build DOM once
    root.innerHTML = '' +
      '<div class="spw-card">' +
        '<div class="spw-card-stripe"></div>' +
        '<button class="spw-close" aria-label="Close" data-action="close">' + ICONS.close + '</button>' +
        '<div class="spw-body">' +
          '<h3 class="spw-title">' + escapeHtml(config.title) + '</h3>' +
          '<p class="spw-subtitle">' + escapeHtml(config.subtitle) + '</p>' +
          '<div class="spw-wheel-wrap">' +
            '<div class="spw-pointer"></div>' +
            '<div class="spw-wheel-container">' +
              '<canvas class="spw-canvas" width="440" height="440"></canvas>' +
            '</div>' +
          '</div>' +
          '<p class="spw-status" data-role="status">Enter your email and mobile to unlock the wheel.</p>' +
          '<div data-role="content"></div>' +
        '</div>' +
      '</div>';

    var statusEl = root.querySelector('[data-role="status"]');
    var contentEl = root.querySelector('[data-role="content"]');
    var canvas = root.querySelector('.spw-canvas');
    var ctx = setupCanvas(canvas);
    drawWheel(ctx, 0, segments, config.colors);

    // ---- Render phases ----
    function renderEmailForm() {
      var phoneHtml = config.capturePhone ? (
        '<div class="spw-input-row">' +
          ICONS.phone +
          '<input class="spw-input" type="tel" inputmode="tel" autocomplete="tel" ' +
            'placeholder="+962 7x xxx xxxx" aria-label="Mobile number" data-role="phone" />' +
        '</div>'
      ) : '';
      var consentHtml = config.consentText ? (
        '<p class="spw-consent-note">' + escapeHtml(config.consentText) + '</p>'
      ) : '';

      contentEl.innerHTML = '' +
        '<form class="spw-form" data-role="email-form">' +
          '<div class="spw-input-row">' +
            ICONS.mail +
            '<input class="spw-input" type="email" inputmode="email" autocomplete="email" ' +
              'placeholder="you@example.com" aria-label="Email address" data-role="email" />' +
          '</div>' +
          phoneHtml +
          consentHtml +
          '<p class="spw-field-error" data-role="email-error" style="display:none"></p>' +
          '<button class="spw-btn" type="submit" disabled data-role="unlock-btn">' +
            '<span data-role="unlock-text">Unlock & Spin</span>' + ICONS.arrow +
          '</button>' +
          '<p class="spw-disclaimer">One spin per device. Discount prizes are capped at 3%.</p>' +
        '</form>';

      var form = contentEl.querySelector('[data-role="email-form"]');
      var input = contentEl.querySelector('[data-role="email"]');
      var phoneInput = contentEl.querySelector('[data-role="phone"]');
      var btn = contentEl.querySelector('[data-role="unlock-btn"]');
      var errEl = contentEl.querySelector('[data-role="email-error"]');

      var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      var PHONE_RE = /^[0-9+() \-]{7,20}$/;

      function validateLocal(email, phone, showMessage) {
        if (!EMAIL_RE.test(email)) {
          if (showMessage !== false) {
            errEl.textContent = 'Please enter a valid email address.';
            errEl.style.display = 'block';
          }
          input.classList.add('spw-error');
          return false;
        }

        input.classList.remove('spw-error');

        if (config.capturePhone && config.phoneRequired && (!phoneInput || !PHONE_RE.test(phone))) {
          if (showMessage !== false) {
            errEl.textContent = 'Please enter a valid mobile number.';
            errEl.style.display = 'block';
          }
          if (phoneInput) phoneInput.classList.add('spw-error');
          return false;
        }

        if (phoneInput) phoneInput.classList.remove('spw-error');

        errEl.style.display = 'none';
        return true;
      }

      function syncButton() {
        var email = input.value.trim();
        var phone = phoneInput ? phoneInput.value.trim() : '';
        btn.disabled = !validateLocal(email, phone, false);
      }

      input.addEventListener('input', syncButton);
      if (phoneInput) phoneInput.addEventListener('input', syncButton);

      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var val = input.value.trim();
        var phone = phoneInput ? phoneInput.value.trim() : '';
        if (!validateLocal(val, phone, true)) return;
        state.email = val.toLowerCase();
        state.phone = phone;
        setPhase('ready');
      });

      // Only auto-focus the email input when the widget is actually visible.
      // Focusing an input inside a hidden popup causes iOS Safari to pop the
      // keyboard and can scroll the page unexpectedly.
      setTimeout(function () {
        if (!isWidgetVisible()) return;
        try { input.focus({ preventScroll: true }); } catch (e) { input.focus(); }
      }, 50);
    }

    function isWidgetVisible() {
      // Widget is visible if: inline mode (always), or popup mode + overlay is open
      if (config.mode !== 'popup') return true;
      var overlay = root.closest('.spw-overlay');
      return !!(overlay && overlay.classList.contains('spw-open'));
    }

    function renderReady() {
      contentEl.innerHTML = '' +
        '<p style="margin-bottom:12px;font-size:13px;color:var(--spw-darkblue)">' +
          'Ready as <strong style="color:var(--spw-black)">' + escapeHtml(state.email) + '</strong>' +
          (state.phone ? '<br><span style="font-size:12px">SMS: ' + escapeHtml(state.phone) + '</span>' : '') +
        '</p>' +
        '<button class="spw-btn spw-btn--spin" data-role="spin-btn">SPIN!</button>' +
        '<button class="spw-btn spw-btn--secondary" style="margin-top:10px;background:transparent;color:var(--spw-darkblue);border:none;box-shadow:none;font-weight:500;font-size:12px" data-role="change-email">Edit details</button>';

      contentEl.querySelector('[data-role="spin-btn"]').addEventListener('click', doSpin);
      contentEl.querySelector('[data-role="change-email"]').addEventListener('click', function () {
        state.email = '';
        state.phone = '';
        setPhase('email');
      });
    }

    function renderSpinning() {
      contentEl.innerHTML = '<p style="font-size:14px;font-weight:600;color:var(--spw-darkblue)">Spinning for ' + escapeHtml(state.email) + '...</p>';
    }

    function renderRevealed() {
      var r = state.result;
      var html = '<div class="spw-result">' +
        '<div class="spw-result-icon ' + (r.isWinner ? 'spw-result-icon--win' : 'spw-result-icon--lose') + '">' +
          (r.isWinner ? ICONS.gift : ICONS.sparkle) +
        '</div>' +
        '<h4 class="spw-result-title">' + (r.isWinner ? 'You Won!' : 'Better Luck Next Time!') + '</h4>' +
        '<span class="spw-result-chip">' + escapeHtml(r.label) + '</span>' +
        '<p class="spw-result-desc">' + escapeHtml(r.description) + '</p>';

      if (r.code) {
        html += '' +
          '<div class="spw-code-row">' +
            '<div class="spw-code-box" data-role="code-box">' + escapeHtml(r.code) + '</div>' +
            '<button class="spw-copy-btn" data-role="copy-btn">' + ICONS.copy + '<span>Copy</span></button>' +
          '</div>' +
          '<p class="spw-code-hint">Apply this code at checkout on your next order.</p>';
      }

      html += '' +
        '<button class="spw-btn spw-btn--secondary" style="margin-top:8px" data-role="done-btn">' +
          (r.code ? 'Done' : 'Maybe next time') +
        '</button>' +
      '</div>';

      contentEl.innerHTML = html;

      if (r.code) {
        var copyBtn = contentEl.querySelector('[data-role="copy-btn"]');
        var codeBox = contentEl.querySelector('[data-role="code-box"]');
        copyBtn.addEventListener('click', function () {
          copyToClipboard(r.code, function () {
            copyBtn.innerHTML = ICONS.check + '<span>Copied!</span>';
            setTimeout(function () {
              copyBtn.innerHTML = ICONS.copy + '<span>Copy</span>';
            }, 2200);
          });
        });
        codeBox.addEventListener('click', function () { copyBtn.click(); });
      }

      contentEl.querySelector('[data-role="done-btn"]').addEventListener('click', function () {
        closePopup();
      });
    }

    function renderPreviousResult(prev) {
      // When device or email already spun — show the existing result
      var isWinner = prev.isWinner;
      var label = PRIZE_LABELS[prev.prize] || prev.label || 'Your prize';
      var description = isWinner
        ? "This device (or email) has already been used to spin. Here's your existing code."
        : "This device (or email) has already been used to spin. Come back next time!";

      state.result = {
        label: label,
        description: description,
        code: prev.code,
        isWinner: isWinner,
      };
      setPhase('revealed');
    }

    function setPhase(phase) {
      state.phase = phase;
      // Clear error state on phase transition; errors are set explicitly via showError()
      statusEl.style.color = '';
      statusEl.textContent =
        phase === 'email' ? 'Enter your email and mobile to unlock the wheel.' :
        phase === 'ready' ? 'Tap SPIN below to play!' :
        phase === 'spinning' ? 'Spinning... good luck!' :
        phase === 'revealed' ? (state.result && state.result.isWinner ? 'You won!' : 'No luck this time.') :
        '';
      if (phase === 'email') renderEmailForm();
      else if (phase === 'ready') renderReady();
      else if (phase === 'spinning') renderSpinning();
      else if (phase === 'revealed') renderRevealed();
    }

    function showError(message) {
      // Show an error message in the status line WITHOUT re-rendering the form,
      // so the user can edit their email and try again.
      statusEl.textContent = message;
      statusEl.style.color = '#dc2626';
    }

    function captureLead(result) {
      var payload = {
        email: state.email,
        phone: state.phone,
        result: result,
        deviceId: deviceId
      };

      if (typeof config.onResult === 'function') {
        try { config.onResult(payload); } catch (e) {}
      }

      suppressHaloNewsletterPopup(config);

      if (!config.leadCaptureFormSelector) return;
      if (config.apiEndpoint && config.submitLeadCaptureForm === false) return;
      var form = document.querySelector(config.leadCaptureFormSelector);
      if (!form) return;

      var emailInput = form.querySelector('[name="contact[email]"]');
      var phoneInput = form.querySelector('[name="contact[Phone]"]');
      var tagsInput = form.querySelector('[name="contact[tags]"]');
      var acceptsInput = form.querySelector('[name="contact[accepts_marketing]"]');
      var prizeTag = 'compume-wheel-' + slugify(result.label || result.prize || 'result');
      if (emailInput) emailInput.value = state.email;
      if (phoneInput) phoneInput.value = state.phone;
      if (tagsInput) tagsInput.value = [config.leadTags, prizeTag].filter(Boolean).join(', ');
      if (acceptsInput) acceptsInput.value = 'true';

      try {
        fetch(form.action || '/contact', {
          method: 'POST',
          body: new FormData(form),
          credentials: 'same-origin',
          headers: { Accept: 'text/html' }
        }).catch(function () {});
      } catch (e) {}
    }

    // ---- Spin action ----
    function doSpin() {
      if (state.busy) return;
      state.busy = true;
      state.error = null;
      setPhase('spinning');

      var spinRequest = (config.useLocalResults || !config.apiEndpoint)
        ? Promise.resolve(createLocalSpinResult(segments))
        : fetch(config.apiEndpoint, {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              email: state.email,
              phone: state.phone,
              deviceId: deviceId,
              source: config.source || config.mode || 'wheel'
            }),
          }).then(function (res) {
            return res.json().catch(function () {
              return {
                ok: false,
                code: 'BAD_RESPONSE',
                error: 'We could not save your spin. Please try again.'
              };
            }).then(function (data) {
              if (!res.ok && data.ok !== false) {
                data = {
                  ok: false,
                  code: 'REQUEST_FAILED',
                  error: 'We could not save your spin. Please try again.'
                };
              }
              return data;
            });
          });

      spinRequest
        .then(function (data) {
          if (!data.ok) {
            state.busy = false;
            if (data.code === 'DEVICE_ALREADY_SPUN' || data.code === 'EMAIL_ALREADY_SPUN') {
              if (data.previousResult) {
                setStorageTs(config.spunKey, Date.now());
                renderPreviousResult(data.previousResult);
              } else {
                setPhase('ready');
                showError(data.error || 'This spin has already been used.');
              }
              return;
            }
            // Show error inline on the ready form so user can change email & retry
            setPhase('ready');
            showError(data.error || 'Something went wrong.');
            return;
          }
          // Animate to the chosen segment
          animateSpin(ctx, data.winnerIndex, segments, config.colors, function () {
            state.result = {
              prize: data.prize,
          label: data.label,
          description: data.description,
          code: data.code,
          isWinner: data.isWinner,
            };
            state.busy = false;
            setStorageTs(config.spunKey, Date.now());
            setStorageJson(config.resultKey, state.result);
            captureLead(state.result);
            setPhase('revealed');
          });
        })
        .catch(function () {
          state.busy = false;
          setPhase('ready');
          showError('Network error. Please try again.');
        });
    }

    var alreadySpunTs = getStorageTs(config.spunKey);
    if (alreadySpunTs > 0 && config.mode !== 'popup') {
      var savedResult = getStorageJson(config.resultKey);
      renderPreviousResult(savedResult || {
        label: 'Already spun',
        description: 'This device has already used its spin. Watch your inbox and SMS for the next CompuMe deal drop.',
        code: '',
        isWinner: false
      });
    } else {
      setPhase('email');
    }
    return { closePopup: closePopup, openPopup: openPopup };
  }

  /* ------------------------- Popup wrapper ------------------------- */
  var popupInstance = null;
  var popupRoot = null;
  var popupConfig = null;

  function ensurePopup() {
    if (popupRoot) return popupRoot;
    popupRoot = document.createElement('div');
    popupRoot.className = 'spw-overlay';
    document.body.appendChild(popupRoot);
    return popupRoot;
  }

  function mount(root, opts) {
    if (opts && opts.mode === 'popup') {
      return mountPopup(opts);
    }
    return createWidget(root, opts);
  }

  function mountPopup(opts) {
    var config = Object.assign({}, DEFAULTS, opts);
    config.colors = normalizeColors(config.colors);
    popupConfig = config;
    suppressHaloNewsletterPopup(config);

    // Already spun this device? Don't even mount the popup.
    var spunTs = getStorageTs(config.spunKey);
    if (spunTs > 0) return null;

    // Was dismissed recently? Respect cooldown. 0 means never show again.
    if (popupDismissedBlocked(config)) return null;

    var overlay = ensurePopup();
    if (popupInstance) return popupInstance;

    overlay.innerHTML = '';
    var inner = document.createElement('div');
    overlay.appendChild(inner);

    popupInstance = createWidget(inner, config);

    // Close on overlay click
    overlay.addEventListener('click', function (e) {
      if (e.target === overlay) closePopup();
    });
    // Close on Esc
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && overlay.classList.contains('spw-open')) closePopup();
    });
    // Close button inside the widget
    overlay.addEventListener('click', function (e) {
      var target = e.target.closest('[data-action="close"]');
      if (target) closePopup();
    });

    // Trigger logic
    if (config.popupTrigger === 'delay') {
      setTimeout(openPopup, config.popupDelayMs);
    } else if (config.popupTrigger === 'exit') {
      attachExitIntent(openPopup);
    }
    // 'manual' trigger — caller will call SpinWheel.openPopup()

    return popupInstance;
  }

  var popupAutoCloseTimer = null;

  function openPopup() {
    var config = popupConfig || DEFAULTS;
    if (!popupRoot) return;
    if (getStorageTs(config.spunKey) > 0) return; // already spun, don't show
    if (popupDismissedBlocked(config)) return;
    // CRITICAL: scroll to top first so the popup is actually visible.
    // Without this, if the user scrolled down a long page, the popup opens
    // centered on their current viewport but body scroll is locked — making
    // the page feel 'halted' (they can't scroll, can't see the popup).
    try {
      window.scrollTo({ top: 0, behavior: 'auto' });
    } catch (e) {
      window.scrollTo(0, 0);
    }
    popupRoot.classList.add('spw-open');
    document.body.style.overflow = 'hidden';
    // Notify external listeners (e.g., the React wrapper) so they don't need to poll the DOM
    if (typeof config.onPopupOpen === 'function') {
      try { config.onPopupOpen(); } catch (e) {}
    }
    // Safety: auto-close after a timeout if configured (prevents stuck states)
    if (config.popupAutoCloseMs > 0) {
      clearTimeout(popupAutoCloseTimer);
      popupAutoCloseTimer = setTimeout(function () { closePopup(); }, config.popupAutoCloseMs);
    }
  }

  function closePopup() {
    var config = popupConfig || DEFAULTS;
    if (!popupRoot) return;
    popupRoot.classList.remove('spw-open');
    document.body.style.overflow = '';
    setStorageTs(config.dismissedKey, Date.now());
    clearTimeout(popupAutoCloseTimer);
    if (typeof config.onPopupClose === 'function') {
      try { config.onPopupClose(); } catch (e) {}
    }
  }

  function attachExitIntent(onTrigger) {
    var fired = false;
    function handleMouseOut(e) {
      if (fired) return;
      // e.relatedTarget is null when leaving the document entirely
      if (!e.relatedTarget && e.clientY <= 0) {
        fired = true;
        onTrigger();
        document.removeEventListener('mouseout', handleMouseOut);
      }
    }
    document.addEventListener('mouseout', handleMouseOut);

    // Mobile fallback: trigger on scroll-up after scrolling down at least 30%
    var lastY = window.scrollY;
    var scrolledDown = false;
    window.addEventListener('scroll', function () {
      var y = window.scrollY;
      if (y > lastY && y > window.innerHeight * 0.3) scrolledDown = true;
      if (y < lastY - 50 && scrolledDown && !fired) {
        fired = true;
        onTrigger();
      }
      lastY = y;
    }, { passive: true });
  }

  /* --------------------------- Helpers --------------------------- */
  function escapeHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function slugify(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '') || 'result';
  }

  function copyToClipboard(text, onDone) {
    if (global.navigator && navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(onDone, fallbackCopy);
    } else {
      fallbackCopy();
    }
    function fallbackCopy() {
      var ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch (e) {}
      document.body.removeChild(ta);
      onDone();
    }
  }

  /* --------------------------- Public API --------------------------- */

  // Auto-inject the widget's CSS if it hasn't been loaded yet.
  // This makes the widget truly self-contained: just drop widget.js on the
  // page and call SpinWheel.mount() — no separate <link> tag needed.
  // The CSS path is derived from this script's src URL.
  function autoInjectCss() {
    if (document.querySelector('link[data-spw-css]')) return; // already injected
    if (document.querySelector('style[data-spw-css]')) return; // already inlined
    if (document.querySelector('link[href*="widget.css"]')) return; // loaded by theme/app block

    // Try to find this script's src to derive the CSS path
    var scripts = document.getElementsByTagName('script');
    var scriptSrc = null;
    for (var i = scripts.length - 1; i >= 0; i--) {
      var s = scripts[i];
      if (s.src && s.src.indexOf('widget.js') >= 0) {
        scriptSrc = s.src;
        break;
      }
    }

    if (scriptSrc) {
      var cssHref = scriptSrc.replace(/widget\.js(\?.*)?$/, 'widget.css');
      var link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = cssHref;
      link.setAttribute('data-spw-css', '1');
      document.head.appendChild(link);
    } else {
      // Fallback: inject critical CSS inline so the popup at least works
      // (overlay must be position:fixed, otherwise body overflow:hidden halts the page)
      var style = document.createElement('style');
      style.setAttribute('data-spw-css', '1');
      style.textContent = CRITICAL_CSS;
      document.head.appendChild(style);
    }
  }

  // Minimal CSS needed to prevent the "halt" bug — full styling comes from widget.css
  var CRITICAL_CSS =
    '.spw-overlay{position:fixed;inset:0;z-index:999999;opacity:0;pointer-events:none;transition:opacity 240ms ease}' +
    '.spw-overlay.spw-open{opacity:1;pointer-events:auto}' +
    '.spw-card{position:relative;background:#fff;border-radius:16px;max-width:520px;width:100%;max-height:92vh;overflow-y:auto;margin:auto}' +
    '.spw-root *{box-sizing:border-box;margin:0;padding:0}';

  // Inject CSS as early as possible (before mount is called)
  if (typeof document !== 'undefined') {
    autoInjectCss();
  }

  global.SpinWheel = {
    mount: mount,
    openPopup: openPopup,
    closePopup: closePopup,
    _uuidv4: uuidv4, // exposed for testing
    _autoInjectCss: autoInjectCss,
  };
})(typeof window !== 'undefined' ? window : this);
